import { createServerFn } from "@tanstack/react-start";

export interface DealerSignupAlertInput {
  email?: string | null;
  step: string;
  errorField?: string | null;
  errorMessage?: string | null;
  userAgent?: string | null;
  viewport?: string | null;
  metadata?: Record<string, unknown> | null;
}

const esc = (v: unknown) =>
  String(v ?? "-").replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" })[c] || c);

/**
 * Sends an admin alert when a dealer gets stuck in the /dealeri signup wizard.
 * Fire-and-forget: never throws to the caller.
 */
export const alertDealerSignupBlock = createServerFn({ method: "POST" })
  .inputValidator((data: DealerSignupAlertInput) => {
    if (!data || typeof data.step !== "string" || !data.step) {
      throw new Error("invalid_step");
    }
    return {
      email: data.email ? String(data.email).slice(0, 254) : null,
      step: data.step.slice(0, 64),
      errorField: data.errorField ? String(data.errorField).slice(0, 64) : null,
      errorMessage: data.errorMessage ? String(data.errorMessage).slice(0, 500) : null,
      userAgent: data.userAgent ? String(data.userAgent).slice(0, 400) : null,
      viewport: data.viewport ? String(data.viewport).slice(0, 32) : null,
      metadata: data.metadata ?? null,
    };
  })
  .handler(async ({ data }) => {
    const apiKey = process.env["RESEND_API_KEY"];
    if (!apiKey) {
      console.warn("[dealer-signup-alert] RESEND_API_KEY missing");
      return { ok: false as const, reason: "email_not_configured" };
    }
    const to = process.env["ADMIN_BCC_EMAIL"] || "contact@roautomarket.ro";

    const html = `
      <div style="font-family:Arial,Helvetica,sans-serif;max-width:600px;margin:0 auto;color:#0f2733">
        <h2 style="color:#E14B3D;margin:0 0 12px">Dealer blocat la înregistrare</h2>
        <p style="margin:0 0 16px">Cineva s-a oprit în wizardul <strong>/dealeri</strong>.</p>
        <table style="width:100%;border-collapse:collapse;font-size:14px">
          <tr><td style="padding:6px 0"><strong>Email</strong></td><td>${esc(data.email)}</td></tr>
          <tr><td style="padding:6px 0"><strong>Pas</strong></td><td>${esc(data.step)}</td></tr>
          <tr><td style="padding:6px 0"><strong>Câmp</strong></td><td>${esc(data.errorField)}</td></tr>
          <tr><td style="padding:6px 0"><strong>Eroare</strong></td><td>${esc(data.errorMessage)}</td></tr>
          <tr><td style="padding:6px 0"><strong>Viewport</strong></td><td>${esc(data.viewport)}</td></tr>
          <tr><td style="padding:6px 0"><strong>User agent</strong></td><td>${esc(data.userAgent)}</td></tr>
          <tr><td style="padding:6px 0;vertical-align:top"><strong>Detalii</strong></td><td><pre style="white-space:pre-wrap;margin:0">${esc(
            JSON.stringify(data.metadata ?? {}, null, 2),
          )}</pre></td></tr>
        </table>
        <p style="margin:20px 0 0;font-size:13px;color:#5b7280">
          Contactează-l rapid — de obicei se recuperează cu un email sau un mesaj scurt.
        </p>
      </div>`;

    try {
      const res = await fetch("https://api.resend.com/emails", {
        method: "POST",
        headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          from: "RO Auto Market <contact@roautomarket.ro>",
          to: [to],
          subject: `Dealer blocat la înregistrare (${data.step})${data.email ? ` — ${data.email}` : ""}`,
          html,
        }),
      });
      if (!res.ok) {
        console.error("[dealer-signup-alert] resend error", await res.text());
        return { ok: false as const, reason: "send_failed" };
      }
      return { ok: true as const };
    } catch (e) {
      console.error("[dealer-signup-alert] failed", e);
      return { ok: false as const, reason: "exception" };
    }
  });
